import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import { timeAgo } from '@/utils/date.util';
import type { AiFlaggedItem } from '@/types/moderation.types';

interface AiFlaggedListProps {
  items: AiFlaggedItem[];
  mutatingId: string | null;
  onView: (item: AiFlaggedItem) => void;
  onDismiss: (item: AiFlaggedItem) => void;
  onRemove: (item: AiFlaggedItem) => void;
}

const actionBtn =
  'cursor-pointer whitespace-nowrap rounded-lg px-3.5 py-[7px] text-[12px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50';

const KIND_PILL = {
  shoutt: { label: '📣 Shoutt', tone: 'info' as const },
  listing: { label: '📦 Listing', tone: 'brand' as const },
};

/** AI Flagged tab — cross-target rows (shoutts + listings) the AI moderation flagged. */
const AiFlaggedList: FC<AiFlaggedListProps> = ({
  items,
  mutatingId,
  onView,
  onDismiss,
  onRemove,
}) => (
  <div className="flex flex-col gap-2.5">
    {items.map((item) => {
      const mutating = mutatingId === item.id;
      const kindPill = KIND_PILL[item.kind];

      return (
        <div
          key={`${item.kind}-${item.id}`}
          className="flex items-start gap-3.5 rounded-xl border border-[var(--color-divider)] border-l-[3px] border-l-[var(--color-pink)] bg-white p-4 transition-all hover:shadow-[var(--shadow-card)]"
        >
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt=""
              className="h-14 w-14 shrink-0 rounded-lg object-cover"
            />
          ) : null}

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Pill tone="pink" className="text-[10px]">🤖 AI Flag</Pill>
              <Pill tone={kindPill.tone} className="text-[10px]">{kindPill.label}</Pill>
              <span className="text-[11px] text-[var(--color-ink-soft)]">· {timeAgo(item.createdAt)}</span>
            </div>

            <button
              type="button"
              onClick={() => onView(item)}
              className="mb-0.5 block max-w-full cursor-pointer truncate text-left text-[13.5px] font-semibold text-[var(--color-ink)] hover:text-brand-600 hover:underline"
            >
              {item.title}
            </button>
            <p className="mb-1.5 truncate text-[11.5px] text-[var(--color-ink-soft)]">
              by {item.authorName} · {item.campusName}
            </p>
            <p className="text-[12.5px] leading-normal text-[var(--color-ink-muted)]">
              {item.flagReason}
            </p>
          </div>

          <div className="flex shrink-0 flex-col gap-1.5">
            <button
              type="button"
              onClick={() => onView(item)}
              className={`${actionBtn} border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]`}
            >
              🔍 Details
            </button>
            <button
              type="button"
              disabled={mutating}
              onClick={() => onRemove(item)}
              className={`${actionBtn} bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white`}
            >
              {mutating ? '…' : '🗑 Remove'}
            </button>
            <button
              type="button"
              disabled={mutating}
              onClick={() => onDismiss(item)}
              className={`${actionBtn} border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]`}
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

export default AiFlaggedList;
